// src/lib/addendums.js
//
// v3.5.99.4 — Shared helpers for the Per-Quote Special Terms (Addendums) panel.
//
// Used by:
//   - All 5 quote pages (load templates, validate before save, build default
//     entry when a rep picks a new template)
//   - AddendumsModal component (validation + default entry)
//   - quoteDiff.js (diffAddendums for QuoteHistory rendering)
//
// Data shapes:
//
//   Template (from addendum_templates table):
//     { id, name, description, body, placeholders[], optional_clauses[],
//       active, sort_order, created_at, updated_at, created_by, updated_by }
//
//   Per-quote addendum entry (lives in quote.inputs.addendums as an array):
//     { template_id, placeholder_values: {key: value, ...}, enabled_clauses: [key, ...] }
//
//   Validation errors (returned by validateAddendums):
//     [{ template_id, template_name, placeholder_key, placeholder_label, reason }]

import { supabase } from './supabase';

/**
 * Load all addendum templates. Filters out inactive by default — pass
 * `{includeInactive: true}` if rendering the full audit list. Ordered by
 * sort_order ascending so the picker shows templates in admin-intended order.
 */
export async function loadAddendumTemplates({ includeInactive = false } = {}) {
  let q = supabase
    .from('addendum_templates')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('name',       { ascending: true });
  if (!includeInactive) q = q.eq('active', true);
  const { data, error } = await q;
  if (error) {
    console.error('loadAddendumTemplates failed:', error);
    return [];
  }
  return data || [];
}

/**
 * Build a default per-quote entry for a newly-selected template. Initializes
 * placeholder_values to empty strings for every declared placeholder, and
 * enabled_clauses to whichever optional_clauses have default_enabled=true.
 *
 * This is what the modal calls when the rep clicks "+ Add" on a template
 * in the picker — gives them a baseline to fill in.
 */
export function defaultEntry(template) {
  if (!template) return null;
  const placeholder_values = {};
  for (const ph of template.placeholders || []) {
    placeholder_values[ph.key] = '';
  }
  const enabled_clauses = (template.optional_clauses || [])
    .filter(c => c.default_enabled)
    .map(c => c.key);
  return {
    template_id: template.id,
    placeholder_values,
    enabled_clauses,
  };
}

/**
 * Validate every entry in an addendums array against its corresponding
 * template. Returns an array of error objects — empty array means valid.
 *
 * Validation rules:
 *   1. Every declared placeholder on the template must have a non-empty value
 *      (trimmed). Missing/whitespace-only values produce an 'empty_placeholder'
 *      error.
 *   2. If the entry references a template_id that doesn't exist in the
 *      templates array (e.g., template was deleted between save and load),
 *      we emit a 'template_not_found' error so the rep can clean up the stale
 *      reference.
 *
 * Used by:
 *   - AddendumsModal Save button (block close until clean)
 *   - Each quote page's save handler (block save until clean — belt + suspenders)
 */
export function validateAddendums(addendums, templates) {
  if (!Array.isArray(addendums) || addendums.length === 0) return [];
  if (!Array.isArray(templates))  return [];
  const errors = [];
  for (const entry of addendums) {
    const template = templates.find(t => t.id === entry.template_id);
    if (!template) {
      errors.push({
        template_id: entry.template_id,
        template_name: '(deleted template)',
        placeholder_key: null,
        placeholder_label: null,
        reason: 'template_not_found',
      });
      continue;
    }
    for (const ph of template.placeholders || []) {
      const value = entry.placeholder_values?.[ph.key];
      if (!value || !String(value).trim()) {
        errors.push({
          template_id: template.id,
          template_name: template.name,
          placeholder_key: ph.key,
          placeholder_label: ph.label || ph.key,
          reason: 'empty_placeholder',
        });
      }
    }
  }
  return errors;
}

/**
 * Build a human-readable summary of the addendums array for QuoteHistory
 * diff rendering. Returns a string like:
 *   "[Supersedes Prior Agreements (with: prior_agreements_list, replacement_services; clauses: revenue_replacement), Month-to-Month Renewal (with: notice_days)]"
 *   or "[]" if empty.
 *
 * The returned string is what QuoteHistory will display as the "from" or
 * "to" value. It's not a structured diff — quoteDiff.js compares the
 * summary strings and shows the full before/after. Reps can open the
 * specific version in QuoteHistory to see line-by-line state if they need
 * finer detail than the summary provides.
 */
export function summarizeAddendums(addendums, templates) {
  if (!Array.isArray(addendums) || addendums.length === 0) return '[]';
  if (!Array.isArray(templates)) templates = [];
  const parts = addendums.map(entry => {
    const t = templates.find(t => t.id === entry.template_id);
    const name = t?.name || `(template ${entry.template_id?.slice(0, 8)}…)`;
    const phKeys = Object.keys(entry.placeholder_values || {})
      .filter(k => entry.placeholder_values[k] && String(entry.placeholder_values[k]).trim());
    const clauseKeys = Array.isArray(entry.enabled_clauses) ? entry.enabled_clauses : [];
    const bits = [];
    if (phKeys.length > 0)     bits.push(`with: ${phKeys.join(', ')}`);
    if (clauseKeys.length > 0) bits.push(`clauses: ${clauseKeys.join(', ')}`);
    return bits.length > 0 ? `${name} (${bits.join('; ')})` : name;
  });
  return `[${parts.join(', ')}]`;
}

/**
 * Compute a quoteDiff-shaped change entry for the addendums field. Returns:
 *   - null if the two arrays are effectively equal
 *   - { label, from, to } otherwise — same shape QuoteHistory expects for
 *     primitive field changes
 *
 * Equality is by JSON.stringify of the structured array (template_id,
 * placeholder_values, enabled_clauses). Order matters — if a rep
 * reorders addendums in the modal, that surfaces as a change. Acceptable
 * since the SO renderer (v3.5.99.5) will render in array order; reordering
 * is a real semantic change worth recording.
 */
export function diffAddendums(oldAddendums, newAddendums, templates) {
  const a = Array.isArray(oldAddendums) ? oldAddendums : [];
  const b = Array.isArray(newAddendums) ? newAddendums : [];
  if (JSON.stringify(a) === JSON.stringify(b)) return null;
  return {
    label: 'Special Terms (Addendums)',
    from:  summarizeAddendums(a, templates),
    to:    summarizeAddendums(b, templates),
  };
}
